import { apiFetch } from '@/lib/api';

// REST API response types
export interface CventRfp {
  id: string;
  name: string;
  code?: string;
  type?: string;
  status?: string;
  eventStartDate?: string;
  eventEndDate?: string;
  decisionDate?: string;
  responseDueDate?: string;
  organizationName?: string;
  [key: string]: any; // Allow other fields from REST API
}

export interface CventRfpListResponse {
  rfps: CventRfp[];
  totalCount: number;
  fetchedCount: number;
}

export class CventService {
  /**
   * Fetch list of RFPs from Cvent REST API
   */
  async fetchRfpList(params?: {
    searchString?: string | null;
    limit?: number;
    statuses?: string[];
    types?: string[];
    bookingTypes?: string[];
  }): Promise<CventRfpListResponse> {
    const queryParams = new URLSearchParams();
    if (params?.searchString) queryParams.set('searchString', params.searchString);
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.statuses) params.statuses.forEach(s => queryParams.append('statuses', s));
    if (params?.types) params.types.forEach(t => queryParams.append('types', t));
    if (params?.bookingTypes) params.bookingTypes.forEach(b => queryParams.append('bookingTypes', b));

    const url = `/api/cvent/rfps${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiFetch(url);
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to fetch RFPs' }));
      throw new Error(error.message || 'Failed to fetch RFPs');
    }
    
    const result = await response.json();
    return result.data; // Returns { rfps, totalCount, fetchedCount }
  }

  /**
   * Fetch details for a specific RFP
   */
  async fetchRfpDetails(
    rfpId: string,
    options?: {
      includeCustomQuestions?: boolean;
      includeResponseOverview?: boolean;
      localeCode?: string;
    }
  ): Promise<unknown> {
    const queryParams = new URLSearchParams();
    if (options?.includeCustomQuestions !== undefined) {
      queryParams.set('includeCustomQuestions', String(options.includeCustomQuestions));
    }
    if (options?.includeResponseOverview !== undefined) {
      queryParams.set('includeResponseOverview', String(options.includeResponseOverview));
    }
    if (options?.localeCode) {
      queryParams.set('localeCode', options.localeCode);
    }

    const url = `/api/cvent/rfps/${rfpId}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiFetch(url);
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to fetch RFP details' }));
      throw new Error(error.message || 'Failed to fetch RFP details');
    }
    
    const result = await response.json();
    return result.data;
  }

  /**
   * Fetch detailed venue/supplier response/proposal
   */
  async fetchVenueResponse(
    rfpId: string,
    supplierId: string,
    localeCode: string = 'en-US'
  ): Promise<unknown> {
    const queryParams = new URLSearchParams();
    if (localeCode) {
      queryParams.set('localeCode', localeCode);
    }

    const url = `/api/cvent/rfps/${rfpId}/suppliers/${supplierId}/response${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiFetch(url);
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to fetch venue response' }));
      throw new Error(error.message || 'Failed to fetch venue response');
    }
    
    const result = await response.json();
    return result.data;
  }

  /**
   * Fetch supplier proposal/response data
   * Returns proposal status, pricing, and other details
   */
  async fetchSupplierProposal(
    rfpId: string,
    supplierId: string,
    localeCode: string = 'en-US'
  ): Promise<unknown> {
    const queryParams = new URLSearchParams();
    if (localeCode) {
      queryParams.set('localeCode', localeCode);
    }

    const url = `/api/cvent/rfps/${rfpId}/suppliers/${supplierId}/response${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiFetch(url);
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to fetch supplier proposal' }));
      throw new Error(error.message || 'Failed to fetch supplier proposal');
    }
    
    const result = await response.json();
    return result.data;
  }

  /**
   * Fetch all attachments for a specific supplier
   */
  async fetchSupplierAttachments(
    rfpId: string,
    supplierId: string
  ): Promise<unknown> {
    const url = `/api/cvent/rfps/${rfpId}/suppliers/${supplierId}/attachments`;
    const response = await apiFetch(url);
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to fetch attachments' }));
      throw new Error(error.message || 'Failed to fetch attachments');
    }
    
    const result = await response.json();
    return result.data;
  }

  /**
   * Download proposal PDF for a specific venue/supplier
   */
  async downloadProposalPdf(rfpId: string, supplierId: string): Promise<Blob> {
    const response = await apiFetch(`/api/cvent/rfps/${rfpId}/suppliers/${supplierId}/proposal.pdf`);
    
    if (!response.ok) {
      const error = await response.text().catch(() => 'Failed to download proposal PDF');
      throw new Error(error || 'Failed to download proposal PDF');
    }
    
    return await response.blob();
  }
}

